import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Button,
  Typography,
  Grid,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  Chip,
  Alert,
  Divider,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Collapse,
  Card,
  CardContent,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Swal from "sweetalert2";
import axios from "axios";
import { setupManagementUrl, salesManagement, companyManagementUrl } from "../../../config";
import { graphqlQuery } from "../../../data/Axios/DynamicService";
import { allCompanyBranches, ColorsQuery } from "../../../data/Axios/queries";
import { companyFields } from "../../../data/DynamicTable/CompanyManagementForms";

const UnifiedDistribution = ({ open, onClose, data, onAction }) => {
  // State for color distributions (warehouse level)
  const [colorDistributions, setColorDistributions] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);
  
  const [baseQuantity, setBaseQuantity] = useState(0);
  const [remainingQuantity, setRemainingQuantity] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [expandedColors, setExpandedColors] = useState({});

  const swalTop = Swal.mixin({
    didOpen: () => {
      const swalContainer = document.querySelector(".swal2-container");
      if (swalContainer) {
        swalContainer.style.zIndex = "99999";
      }
    },
  });

  // Extract product details - handle both direct data and nested purchaseOrderItemViewById
  const itemData = data?.purchaseOrderItemViewById?.[0] || data;
  const productCode = itemData?.productCode || "";
  const productName = itemData?.productName || "";
  const aliasProductName = itemData?.aliasProductName || "";
  const defaultCartons = itemData?.cartons || "";
  const minShoeSize = itemData?.minShoeSize || 0;
  const maxShoeSize = itemData?.maxShoeSize || 100;
  const purchaseOrderId = itemData?.purchaseOrderId || itemData?.id;
  const productId = itemData?.productId;
  const purchaseOrderItemId = itemData?.id;
  const branchId = itemData?.branchId; // Get branch from data prop

  // Fetch all required data
  const fetchData = async () => {
    try {
      const colorData = await graphqlQuery(ColorsQuery, setupManagementUrl.uri);
      if (colorData) {
        setAvailableColors(colorData);
      }
      // Removed branch fetching - using branchId from data prop
    } catch (error) {
      console.error("Error fetching options:", error);
      swalTop.fire({
        icon: "error",
        title: "Failed to Load Options",
        text: error.response?.data?.errors?.[0]?.message || error.message,
      });
    }
  };

  // Initialize on dialog open
  useEffect(() => {
    if (open) {
      const quantity = Number(itemData?.quantity) || 0;
      setBaseQuantity(quantity);
      setRemainingQuantity(quantity);

      // Get companyId from decoded token
      const decodedTokenStr = localStorage.getItem("decodedToken");
      const decodedToken = decodedTokenStr ? JSON.parse(decodedTokenStr) : {};
      const companyId = decodedToken.companyId;

      // Initialize with one color distribution (no branch selection needed)
      setColorDistributions([
        {
          id: 1,
          colorId: "",
          colorName: "",
          companyId: companyId,
          cartons: defaultCartons || "",
          quantityPerCarton: "",
          totalColorQuantity: 0,
          remainingColorQuantity: 0,
          sizeDistributions: [
            {
              id: 1,
              size: "",
              quantity: "",
            },
          ],
        },
      ]);

      setExpandedColors({ 1: true });
      fetchData();
    }
  }, [open, data]);

  // Calculate totals across all color distributions
  const calculateTotals = (distributions) => {
    const totalUsed = distributions.reduce((acc, colorDist) => {
      const cartons = Number(colorDist.cartons) || 0;
      const perCarton = Number(colorDist.quantityPerCarton) || 0;
      return acc + cartons * perCarton;
    }, 0);

    const remaining = Math.max(baseQuantity - totalUsed, 0);
    setRemainingQuantity(remaining);
  };

  // Calculate remaining quantity for a specific color distribution
  const calculateColorRemaining = (colorDist) => {
    const totalColorQty = (Number(colorDist.cartons) || 0) * (Number(colorDist.quantityPerCarton) || 0);
    const usedInSizes = colorDist.sizeDistributions.reduce(
      (acc, size) => acc + (Number(size.quantity) || 0),
      0
    );
    return Math.max(totalColorQty - usedInSizes, 0);
  };

  // Add new color distribution
  const addColorDistribution = () => {
    // Get companyId from decoded token
    const decodedTokenStr = localStorage.getItem("decodedToken");
    const decodedToken = decodedTokenStr ? JSON.parse(decodedTokenStr) : {};
    const companyId = decodedToken.companyId;

    const newId = colorDistributions.length > 0 
      ? Math.max(...colorDistributions.map((d) => d.id)) + 1 
      : 1;
    const newDistributions = [
      ...colorDistributions,
      {
        id: newId,
        colorId: "",
        colorName: "",
        companyId: companyId,
        cartons: defaultCartons || "",
        quantityPerCarton: "",
        totalColorQuantity: 0,
        remainingColorQuantity: 0,
        sizeDistributions: [
          {
            id: 1,
            size: "",
            quantity: "",
          },
        ],
      },
    ];
    setColorDistributions(newDistributions);
    setExpandedColors({ ...expandedColors, [newId]: true });
  };

  // Remove color distribution
  const removeColorDistribution = (id) => {
    if (colorDistributions.length > 1) {
      const updatedDistributions = colorDistributions.filter((dist) => dist.id !== id);
      setColorDistributions(updatedDistributions);
      calculateTotals(updatedDistributions);
      
      const newExpanded = { ...expandedColors };
      delete newExpanded[id];
      setExpandedColors(newExpanded);
    }
  };

  // Update color distribution field
  const updateColorDistribution = (id, field, value) => {
    const updatedDistributions = colorDistributions.map((dist) => {
      if (dist.id === id) {
        if (field === "color") {
          const selectedColor = availableColors.find((c) => c.id === value);
          return {
            ...dist,
            colorId: value,
            colorName: selectedColor?.name || "",
          };
        }

        if (field === "cartons" || field === "quantityPerCarton") {
          const updatedDist = { ...dist, [field]: value };
          const newCartons = Number(updatedDist.cartons) || 0;
          const newQtyPerCarton = Number(updatedDist.quantityPerCarton) || 0;
          const newTotal = newCartons * newQtyPerCarton;

          // Calculate total from other color distributions
          const otherTotal = colorDistributions.reduce((acc, d) => {
            if (d.id === id) return acc;
            const cartons = Number(d.cartons) || 0;
            const perCarton = Number(d.quantityPerCarton) || 0;
            return acc + cartons * perCarton;
          }, 0);

          if (otherTotal + newTotal > baseQuantity) {
            const maxAllowed = baseQuantity - otherTotal;
            swalTop.fire({
              icon: "warning",
              title: "Quantity Exceeded",
              text: `This color can only have ${maxAllowed} units. You're trying to allocate ${newTotal} units.`,
              timer: 3000,
            });
            return dist;
          }

          updatedDist.totalColorQuantity = newTotal;
          updatedDist.remainingColorQuantity = calculateColorRemaining(updatedDist);
          return updatedDist;
        }

        return { ...dist, [field]: value };
      }
      return dist;
    });

    setColorDistributions(updatedDistributions);
    calculateTotals(updatedDistributions);
  };

  // Add size distribution to a color
  const addSizeDistribution = (colorId) => {
    const updatedDistributions = colorDistributions.map((dist) => {
      if (dist.id === colorId) {
        const newSizeId = dist.sizeDistributions.length > 0
          ? Math.max(...dist.sizeDistributions.map((s) => s.id)) + 1
          : 1;
        return {
          ...dist,
          sizeDistributions: [
            ...dist.sizeDistributions,
            {
              id: newSizeId,
              size: "",
              quantity: "",
            },
          ],
        };
      }
      return dist;
    });
    setColorDistributions(updatedDistributions);
  };

  // Remove size distribution
  const removeSizeDistribution = (colorId, sizeId) => {
    const updatedDistributions = colorDistributions.map((dist) => {
      if (dist.id === colorId && dist.sizeDistributions.length > 1) {
        const newSizeDistributions = dist.sizeDistributions.filter((s) => s.id !== sizeId);
        const updatedDist = { ...dist, sizeDistributions: newSizeDistributions };
        updatedDist.remainingColorQuantity = calculateColorRemaining(updatedDist);
        return updatedDist;
      }
      return dist;
    });
    setColorDistributions(updatedDistributions);
  };

  // Update size distribution field
  const updateSizeDistribution = (colorId, sizeId, field, value) => {
    const updatedDistributions = colorDistributions.map((dist) => {
      if (dist.id === colorId) {
        const updatedSizes = dist.sizeDistributions.map((size) => {
          if (size.id === sizeId) {
            if (field === "size") {
              // Allow empty or partial input during typing
              if (value === "") {
                return { ...size, size: value };
              }
              
              const sizeValue = Number(value);
              
              // Check for duplicate size in the same color
              const isDuplicate = dist.sizeDistributions.some(
                (s) => s.id !== sizeId && s.size && Number(s.size) === sizeValue
              );
              
              if (isDuplicate) {
                swalTop.fire({
                  icon: "warning",
                  title: "Duplicate Size",
                  text: `Size ${sizeValue} is already allocated for ${dist.colorName}. Please use a different size.`,
                  timer: 3000,
                });
                return size; // Don't update, keep old value
              }
              
              // Only validate if it's a complete number and user has finished typing
              if (!isNaN(sizeValue) && value.length >= 2) {
                if (sizeValue < minShoeSize || sizeValue > maxShoeSize) {
                  // Don't block the input, but mark it as invalid
                  return { ...size, size: value };
                }
              }
              
              return { ...size, size: value };
            }

            if (field === "quantity") {
              const newQty = Number(value) || 0;
              const otherSizesTotal = dist.sizeDistributions.reduce((acc, s) => {
                if (s.id === sizeId) return acc;
                return acc + (Number(s.quantity) || 0);
              }, 0);

              if (otherSizesTotal + newQty > dist.totalColorQuantity) {
                const maxAllowed = dist.totalColorQuantity - otherSizesTotal;
                swalTop.fire({
                  icon: "warning",
                  title: "Quantity Exceeded",
                  text: `This size can only have ${maxAllowed} units for ${dist.colorName}.`,
                  timer: 3000,
                });
                return size;
              }
            }

            return { ...size, [field]: value };
          }
          return size;
        });

        const updatedDist = { ...dist, sizeDistributions: updatedSizes };
        updatedDist.remainingColorQuantity = calculateColorRemaining(updatedDist);
        return updatedDist;
      }
      return dist;
    });
    setColorDistributions(updatedDistributions);
  };

  // Validate size on blur (when user leaves the field)
  const handleSizeBlur = (colorId, sizeId, value) => {
    if (value === "") return; // Empty is okay during editing
    
    const sizeValue = Number(value);
    
    if (isNaN(sizeValue)) {
      swalTop.fire({
        icon: "warning",
        title: "Invalid Size",
        text: `Please enter a valid number`,
        timer: 2000,
      });
      return;
    }
    
    if (sizeValue < minShoeSize || sizeValue > maxShoeSize) {
      swalTop.fire({
        icon: "warning",
        title: "Invalid Size",
        text: `Size must be between ${minShoeSize} and ${maxShoeSize}`,
        timer: 2000,
      });
      return;
    }
    
    // Check for duplicates on blur as well
    const colorDist = colorDistributions.find((d) => d.id === colorId);
    if (colorDist) {
      const isDuplicate = colorDist.sizeDistributions.some(
        (s) => s.id !== sizeId && s.size && Number(s.size) === sizeValue
      );
      
      if (isDuplicate) {
        swalTop.fire({
          icon: "warning",
          title: "Duplicate Size",
          text: `Size ${sizeValue} is already used for ${colorDist.colorName}. Please choose a different size.`,
          timer: 3000,
        });
      }
    }
  };

  // Check if size is duplicate within the same color
  const isSizeDuplicate = (colorId, currentSizeId, sizeValue) => {
    if (!sizeValue) return false;
    const colorDist = colorDistributions.find((d) => d.id === colorId);
    if (!colorDist) return false;
    
    const duplicates = colorDist.sizeDistributions.filter(
      (s) => s.id !== currentSizeId && s.size && Number(s.size) === Number(sizeValue)
    );
    
    return duplicates.length > 0;
  };

  // Toggle color expansion
  const toggleColorExpansion = (colorId) => {
    setExpandedColors({
      ...expandedColors,
      [colorId]: !expandedColors[colorId],
    });
  };

  // Get selected color IDs
  const getSelectedColorIds = (currentColorId) => {
    return colorDistributions
      .filter((dist) => dist.id !== currentColorId && dist.colorId)
      .map((dist) => dist.colorId);
  };

  // Validate form
  const validateForm = () => {
    // Check if all quantities are distributed at product level
    if (remainingQuantity !== 0) {
      swalTop.fire({
        icon: "warning",
        title: "Incomplete Distribution",
        text: `You have ${remainingQuantity} units remaining at product level. Please distribute all quantities.`,
      });
      return false;
    }

    // Validate each color distribution
    for (let i = 0; i < colorDistributions.length; i++) {
      const colorDist = colorDistributions[i];

      if (!colorDist.colorId) {
        swalTop.fire({
          icon: "warning",
          title: "Validation Error",
          text: `Please select a color for distribution ${i + 1}`,
        });
        return false;
      }

      if (!colorDist.cartons || Number(colorDist.cartons) <= 0) {
        swalTop.fire({
          icon: "warning",
          title: "Validation Error",
          text: `Please enter valid cartons for ${colorDist.colorName}`,
        });
        return false;
      }

      if (!colorDist.quantityPerCarton || Number(colorDist.quantityPerCarton) <= 0) {
        swalTop.fire({
          icon: "warning",
          title: "Validation Error",
          text: `Please enter valid quantity per carton for ${colorDist.colorName}`,
        });
        return false;
      }

      // Check if all color quantities are distributed to sizes
      if (colorDist.remainingColorQuantity !== 0) {
        swalTop.fire({
          icon: "warning",
          title: "Incomplete Size Distribution",
          text: `${colorDist.colorName} has ${colorDist.remainingColorQuantity} units remaining. Please distribute all quantities to sizes.`,
        });
        return false;
      }

      // Validate size distributions
      for (let j = 0; j < colorDist.sizeDistributions.length; j++) {
        const sizeDist = colorDist.sizeDistributions[j];

        if (!sizeDist.size || sizeDist.size === "") {
          swalTop.fire({
            icon: "warning",
            title: "Validation Error",
            text: `Please enter a size for ${colorDist.colorName} distribution ${j + 1}`,
          });
          return false;
        }

        const sizeValue = Number(sizeDist.size);
        
        if (isNaN(sizeValue)) {
          swalTop.fire({
            icon: "warning",
            title: "Validation Error",
            text: `Please enter a valid numeric size for ${colorDist.colorName} distribution ${j + 1}`,
          });
          return false;
        }
        
        if (sizeValue < minShoeSize || sizeValue > maxShoeSize) {
          swalTop.fire({
            icon: "warning",
            title: "Validation Error",
            text: `Size ${sizeValue} is out of range. Size must be between ${minShoeSize} and ${maxShoeSize} for ${colorDist.colorName} distribution ${j + 1}`,
          });
          return false;
        }

        // Check for duplicate sizes within the same color
        const duplicateSizes = colorDist.sizeDistributions.filter(
          (s) => s.size && Number(s.size) === sizeValue
        );
        
        if (duplicateSizes.length > 1) {
          swalTop.fire({
            icon: "warning",
            title: "Duplicate Size",
            text: `Size ${sizeValue} is used multiple times for ${colorDist.colorName}. Each size should be unique within a color. Please use different sizes or combine quantities.`,
          });
          return false;
        }

        if (!sizeDist.quantity || Number(sizeDist.quantity) <= 0) {
          swalTop.fire({
            icon: "warning",
            title: "Validation Error",
            text: `Please enter valid quantity for ${colorDist.colorName} size ${sizeDist.size}`,
          });
          return false;
        }
      }
    }

    return true;
  };

  // Submit both warehouse and retail distributions
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const rawToken = localStorage.getItem("token");
      const cleanToken = rawToken?.replace(/^"|"$/g, "") || "";

      const decodedTokenStr = localStorage.getItem("decodedToken");
      const decodedToken = decodedTokenStr ? JSON.parse(decodedTokenStr) : {};
      const createdBy = decodedToken.id;

      // Step 1: Warehouse Distribution
      const warehouseDistributionData = colorDistributions.map((colorDist) => ({
        colorId: parseInt(colorDist.colorId),
        totalquantity: colorDist.totalColorQuantity,
        cartons: Number(colorDist.cartons),
        quantityPerCarton: Number(colorDist.quantityPerCarton),
      }));

      const warehouseMutation = `
        mutation WarehouseDistribution {
          warehouseDistribution(
            newWarehouseDistribution: {
              purchaseOrderId: ${purchaseOrderId},
              purchaseOrderItemsId: ${purchaseOrderItemId},
              distributionData: """${JSON.stringify(warehouseDistributionData)}""",
            }
          ) {
            purchaseOrderId
            distributionData
            createdBy
          }
        }
      `;

      // const warehouseResponse = await axios.post(
      //   salesManagement.uri,
      //   { query: warehouseMutation },
      //   {
      //     headers: {
      //       "Content-Type": "application/json",
      //       Authorization: `Bearer ${cleanToken}`,
      //     },
      //   }
      // );

      // if (warehouseResponse.data.errors) {
      //   throw new Error(warehouseResponse.data.errors[0].message);
      // }

      // Step 2: Retail Distribution (only if warehouse succeeds)
      // Transform data to match GraphQL mutation structure
      const retailInput = colorDistributions
        .map((colorDist) => ({
          purchaseOrderId: purchaseOrderId,
          purchaseOrderItemsId: purchaseOrderItemId,
          companyId: colorDist.companyId || decodedToken.companyId,
          colorId: colorDist.colorId,
          distributions: colorDist.sizeDistributions
            .filter((sizeDist) => sizeDist.size && sizeDist.quantity)
            .map((sizeDist) => ({
              size: String(sizeDist.size),
              quantity: Number(sizeDist.quantity),
            })),
        }))
        .filter((item) => item.distributions.length > 0);

      if (retailInput.length === 0) {
        throw new Error("No valid retail distributions to save");
      }

      // Convert to GraphQL literal string (remove quotes around keys)
      const inputAsGraphQLLiteral = JSON.stringify(retailInput).replace(
        /"([^"]+)":/g,
        "$1:"
      );

      // Build the retail mutation string
      const retailMutation = `
        mutation {
          retailDistribution(distributions: ${inputAsGraphQLLiteral}) {
            purchaseOrderId
            purchaseOrderItemsId
            companyId
            colorId
            size
            quantity
            createdBy
          }
        }
      `;

      const retailResponse = await axios.post(
        salesManagement.uri,
        { query: retailMutation },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${cleanToken}`,
          },
        }
      );

      if (retailResponse.data.errors) {
        throw new Error(retailResponse.data.errors[0].message);
      }

      // Calculate total pairs distributed
      const totalPairs = colorDistributions.reduce(
        (sum, colorDist) => sum + colorDist.totalColorQuantity,
        0
      );

      // Both mutations succeeded
      await swalTop.fire({
        icon: "success",
        title: "Success!",
        text: `✓ Distribution Saved Successfully!\n\n${totalPairs} pairs distributed across ${colorDistributions.length} colors.\n\nYou can view it in Retail Distribution:  Inventory Management> Retail Distributed Logs`,
      });

      onAction?.();
      handleClose();
    } catch (error) {
      console.error("Error creating distribution:", error);
      swalTop.fire({
        icon: "error",
        title: "Error!",
        text: error.response?.data?.errors?.[0]?.message || error.message || "An error occurred while saving distribution.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const isFormValid = 
    remainingQuantity === 0 &&
    colorDistributions.every((colorDist) => 
      colorDist.colorId &&
      colorDist.cartons &&
      colorDist.quantityPerCarton &&
      colorDist.remainingColorQuantity === 0 &&
      colorDist.sizeDistributions.every((sizeDist) => {
        if (!sizeDist.size || sizeDist.size === "") return false;
        const sizeValue = Number(sizeDist.size);
        return (
          !isNaN(sizeValue) &&
          sizeValue >= minShoeSize &&
          sizeValue <= maxShoeSize &&
          sizeDist.quantity &&
          Number(sizeDist.quantity) > 0
        );
      })
    );

  const completedColors = colorDistributions.filter(
    (dist) => dist.remainingColorQuantity === 0 && dist.totalColorQuantity > 0
  ).length;
  const totalColors = colorDistributions.filter((dist) => dist.totalColorQuantity > 0).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <StorefrontIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Unified Product Distribution
            </Typography>
            <Chip
              label={`${completedColors}/${totalColors} Colors Complete`}
              color={completedColors === totalColors && totalColors > 0 ? "success" : "default"}
              size="small"
            />
          </Box>
          <IconButton onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Product Information Section */}
        <Paper elevation={2} sx={{ mb: 3, p: 2.5, bgcolor: "primary.50" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <Typography variant="caption" color="text.secondary" display="block">
                Product Code
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {productCode || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="caption" color="text.secondary" display="block">
                Product Name
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {productName || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="caption" color="text.secondary" display="block">
                Alias Name
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {aliasProductName || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <Typography variant="caption" color="text.secondary" display="block">
                Total Quantity
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {baseQuantity}
              </Typography>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <Typography variant="caption" color="text.secondary" display="block">
                Size Range
              </Typography>
              <Chip 
                label={`${minShoeSize} - ${maxShoeSize}`}
                color="info"
                size="small"
                sx={{ fontWeight: "bold" }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ position: "relative" }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Distribution Progress
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={baseQuantity > 0 ? ((baseQuantity - remainingQuantity) / baseQuantity) * 100 : 0}
                  sx={{ height: 8, borderRadius: 1, mt: 0.5 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  {baseQuantity - remainingQuantity} / {baseQuantity} distributed
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Status Alerts */}
        {remainingQuantity > 0 && (
          <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
            <strong>{remainingQuantity}</strong> units remaining to distribute at product level
          </Alert>
        )}

        {remainingQuantity === 0 && colorDistributions.some((d) => d.remainingColorQuantity > 0) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Product quantities distributed to colors, but some colors have unallocated quantities for sizes
          </Alert>
        )}

        {isFormValid && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            All quantities distributed successfully! Ready to submit.
          </Alert>
        )}

        <Divider sx={{ my: 2 }}>
          <Chip label="Color & Size Distributions" />
        </Divider>

        {/* Color Distributions */}
        <Box sx={{ mt: 3 }}>
          {colorDistributions.map((colorDist, colorIndex) => (
            <Card
              key={colorDist.id}
              elevation={3}
              sx={{
                mb: 3,
                border: colorDist.remainingColorQuantity === 0 && colorDist.totalColorQuantity > 0
                  ? "2px solid #4caf50"
                  : "1px solid #e0e0e0",
              }}
            >
              <CardContent>
                {/* Color Distribution Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                    p: 1.5,
                    bgcolor: "#f5f5f5",
                    borderRadius: 1,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Chip
                      icon={<WarehouseIcon />}
                      label={`Color ${colorIndex + 1}`}
                      color="primary"
                      variant="outlined"
                    />
                    {colorDist.colorName && (
                      <Chip label={colorDist.colorName} color="secondary" />
                    )}
                    {colorDist.totalColorQuantity > 0 && (
                      <Chip
                        label={`${colorDist.totalColorQuantity - colorDist.remainingColorQuantity}/${colorDist.totalColorQuantity} allocated`}
                        color={colorDist.remainingColorQuantity === 0 ? "success" : "warning"}
                        size="small"
                      />
                    )}
                  </Box>
                  <Box display="flex" gap={1}>
                    <Tooltip title={expandedColors[colorDist.id] ? "Collapse" : "Expand"}>
                      <IconButton
                        size="small"
                        onClick={() => toggleColorExpansion(colorDist.id)}
                      >
                        {expandedColors[colorDist.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove Color">
                      <span>
                        <IconButton
                          onClick={() => removeColorDistribution(colorDist.id)}
                          disabled={colorDistributions.length === 1}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>

                <Collapse in={expandedColors[colorDist.id]} timeout="auto">
                  {/* Color Selection and Carton Info */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Color</InputLabel>
                        <Select
                          value={colorDist.colorId}
                          label="Color"
                          onChange={(e) =>
                            updateColorDistribution(colorDist.id, "color", e.target.value)
                          }
                        >
                          <MenuItem value="">
                            <em>Select color</em>
                          </MenuItem>
                          {availableColors.map((color) => {
                            const isDisabled = getSelectedColorIds(colorDist.id).includes(color.id);
                            return (
                              <MenuItem
                                key={color.id}
                                value={color.id}
                                disabled={isDisabled}
                              >
                                {color.name} {isDisabled && "(Selected)"}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Cartons"
                        required
                        value={colorDist.cartons}
                        onChange={(e) =>
                          updateColorDistribution(colorDist.id, "cartons", e.target.value)
                        }
                        inputProps={{ min: 1, readOnly: defaultCartons ? true : false }}
                      />
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Qty/Carton"
                        required
                        value={colorDist.quantityPerCarton}
                        onChange={(e) =>
                          updateColorDistribution(
                            colorDist.id,
                            "quantityPerCarton",
                            e.target.value
                          )
                        }
                        inputProps={{ min: 1 }}
                      />
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Total Color Qty"
                        value={colorDist.totalColorQuantity}
                        InputProps={{ readOnly: true }}
                        sx={{ bgcolor: "#e3f2fd" }}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Remaining for Sizes"
                        value={colorDist.remainingColorQuantity}
                        InputProps={{ readOnly: true }}
                        sx={{
                          bgcolor: colorDist.remainingColorQuantity === 0 ? "#e8f5e9" : "#fff3e0",
                        }}
                        error={colorDist.remainingColorQuantity > 0 && colorDist.totalColorQuantity > 0}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }}>
                    <Chip label={`Size Distribution (Valid: ${minShoeSize}-${maxShoeSize})`} size="small" />
                  </Divider>

                  {/* Size Distributions */}
                  {colorDist.sizeDistributions.map((sizeDist, sizeIndex) => (
                    <Grid
                      container
                      spacing={2}
                      key={sizeDist.id}
                      alignItems="center"
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: "#fafafa",
                        borderRadius: 1,
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <Grid item xs={12} sm={0.5}>
                        <Chip label={sizeIndex + 1} size="small" />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Size"
                          required
                          value={sizeDist.size}
                          onChange={(e) =>
                            updateSizeDistribution(
                              colorDist.id,
                              sizeDist.id,
                              "size",
                              e.target.value
                            )
                          }
                          onBlur={(e) =>
                            handleSizeBlur(
                              colorDist.id,
                              sizeDist.id,
                              e.target.value
                            )
                          }
                          inputProps={{ 
                            min: minShoeSize,
                            max: maxShoeSize
                          }}
                          helperText={
                            isSizeDuplicate(colorDist.id, sizeDist.id, sizeDist.size)
                              ? "⚠️ Duplicate size!"
                              : `Range: ${minShoeSize}-${maxShoeSize}`
                          }
                          error={
                            (sizeDist.size && 
                              (Number(sizeDist.size) < minShoeSize || 
                               Number(sizeDist.size) > maxShoeSize)) ||
                            isSizeDuplicate(colorDist.id, sizeDist.id, sizeDist.size)
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Quantity"
                          required
                          value={sizeDist.quantity}
                          onChange={(e) =>
                            updateSizeDistribution(
                              colorDist.id,
                              sizeDist.id,
                              "quantity",
                              e.target.value
                            )
                          }
                          inputProps={{ min: 1 }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4.5}>
                        <Box display="flex" gap={1} alignItems="center">
                          {colorDist.colorName && sizeDist.size && (
                            <Chip
                              label={`${colorDist.colorName} | Size ${sizeDist.size}`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={1}>
                        <Tooltip title="Remove Size">
                          <span>
                            <IconButton
                              onClick={() =>
                                removeSizeDistribution(colorDist.id, sizeDist.id)
                              }
                              disabled={colorDist.sizeDistributions.length === 1}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  ))}

                  {/* Add Size Button */}
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => addSizeDistribution(colorDist.id)}
                      variant="outlined"
                      size="small"
                      disabled={colorDist.remainingColorQuantity === 0}
                    >
                      Add Size Distribution
                    </Button>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          ))}

          {/* Add Color Button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button
              startIcon={<AddIcon />}
              onClick={addColorDistribution}
              variant="contained"
              disabled={remainingQuantity === 0}
            >
              Add Color Distribution
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, bgcolor: "#f5f5f5" }}>
        <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Product Level: <strong>{baseQuantity - remainingQuantity}/{baseQuantity}</strong> distributed
          </Typography>
          {isFormValid && (
            <Chip
              icon={<CheckCircleIcon />}
              label="Ready to Submit"
              color="success"
              size="small"
            />
          )}
        </Box>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || isSubmitting}
          size="large"
        >
          {isSubmitting ? "Submitting..." : "Submit Distribution"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnifiedDistribution;